import { createSelector } from '@ngrx/store';

import { BlightData, ParticipatingCropField } from '../../blight-v2/models/models';
import { AppState } from '../../shared/models';
import { selectApp } from '../../store/selectors';
import { mapCropFields } from '../models/api-mappers';
import { BlightCropFieldAdvice } from '../models/models';

export const selectBlightData =
  createSelector(selectApp, (store: AppState): BlightData[] => store.blightData);

export const selectParticipatingCropFields =
  createSelector(selectApp, (store: AppState): ParticipatingCropField[] => store.blightParticipatingCropFields);

export const selectIsSelectedCropFieldSyncing =
  createSelector(selectApp, (store: AppState): boolean =>
    !!store.selectedCropField &&
    store.blightRecalculationStatusPerCropField.some(
      (x) => x.isBlightRecalculating && x.cropFieldId === store.selectedCropField!.id,
    ),
  );

export const selectIsCropFieldsSyncing =
  createSelector(selectApp, (store: AppState): boolean =>
    store.blightRecalculationStatusPerCropField.some((x) => x.isBlightRecalculating));

  export const selectVisibleBlightData =
    createSelector(selectBlightData, (cropFields): BlightData[] =>
        cropFields.filter((data) => data.isCropFieldVisible));

export const selectBlightCalculationDate = createSelector(
  selectVisibleBlightData, (blightData): Date | null =>
    blightData.reduce((minDate, blightDataForCropField) => {
      const calculatedOn = blightDataForCropField.blightResult?.calculatedOn
        ? new Date(blightDataForCropField.blightResult.calculatedOn)
        : null;
      return calculatedOn && (!minDate || calculatedOn < minDate) ? calculatedOn : minDate;
    }, null as Date | null),
);

export const selectCropFieldsWithBlightData = createSelector(
  selectBlightData,
  selectParticipatingCropFields,
  (blightData, cropFields): BlightCropFieldAdvice[] =>
    blightData
      .filter((bd) => bd.isCropFieldVisible)
      .map((blightDataForCropField) => {
        const cropField = cropFields.find((x) => x.id === blightDataForCropField.cropFieldId);
        return cropField ? mapCropFields(blightDataForCropField, cropField) : null;
      })
      .filter((cropFieldAdvice): cropFieldAdvice is BlightCropFieldAdvice => cropFieldAdvice !== null)
      .sort((a, b) => a.cropFieldName.localeCompare(b.cropFieldName)),
);

export const selectMinAdviceDate =
  createSelector(selectCropFieldsWithBlightData, (cropFields): Date | undefined => {
    let minDate: Date | undefined = undefined;

    cropFields.forEach((cropField) => {
      if (cropField.plantDate && (!minDate || cropField.plantDate < minDate)) {
        minDate = cropField.plantDate;
      }
    });

    return minDate;
  });
